// TradeHistory/TradeSummary.jsx - TradingView Style Compact Design
import React, { useMemo } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { COLORS } from './constants';

// Compact stat row component
const StatRow = ({ label, value, valueColor = COLORS.text.primary, suffix = '', isLast = false }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 0.5,
      borderBottom: isLast ? 'none' : `1px solid ${alpha(COLORS.border, 0.4)}`,
    }}
  >
    <Typography sx={{ color: COLORS.text.muted, fontSize: '0.68rem' }}>
      {label}
    </Typography>
    <Typography 
      sx={{ 
        color: valueColor, 
        fontSize: '0.72rem', 
        fontWeight: 600, 
        fontFamily: 'monospace',
      }}
    >
      {value}{suffix}
    </Typography>
  </Box>
);

const TradeSummary = ({ stats, trades }) => {
  const computedStats = useMemo(() => {
    if (!trades?.length) {
      return {
        avgAdjustedWinPct: 0,
        avgAdjustedLossPct: 0,
        largestAdjustedWinPct: 0,
        largestAdjustedLossPct: 0,
        adjustedProfitFactor: 0,
        avgDurationMins: 0,
        maxWinStreak: 0,
        maxLossStreak: 0,
      };
    }

    const slippageCostPct = stats.slippageCostPct || 0.05;
    const validTrades = trades.filter(t => t.pnl_pct !== null && t.pnl_pct !== undefined);
    const getAdjustedPct = (t) => (parseFloat(t.pnl_pct) || 0) - slippageCostPct;

    const adjustedWins = validTrades.filter(t => getAdjustedPct(t) > 0);
    const adjustedLosses = validTrades.filter(t => getAdjustedPct(t) <= 0);

    const totalAdjustedWinPct = adjustedWins.reduce((sum, t) => sum + getAdjustedPct(t), 0);
    const totalAdjustedLossPct = Math.abs(adjustedLosses.reduce((sum, t) => sum + getAdjustedPct(t), 0));

    // Streaks
    let currentWinStreak = 0, currentLossStreak = 0, maxWinStreak = 0, maxLossStreak = 0;
    validTrades.forEach(t => {
      if (getAdjustedPct(t) > 0) {
        currentWinStreak++;
        currentLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, currentWinStreak);
      } else {
        currentLossStreak++;
        currentWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, currentLossStreak);
      }
    });

    // Duration
    let totalDurationMs = 0;
    let durationCount = 0;
    validTrades.forEach(t => {
      if (t.entry_time && t.exit_time) {
        try {
          totalDurationMs += new Date(t.exit_time) - new Date(t.entry_time);
          durationCount++;
        } catch {
          // Skip invalid dates
        }
      }
    });

    return {
      avgAdjustedWinPct: adjustedWins.length > 0 ? totalAdjustedWinPct / adjustedWins.length : 0,
      avgAdjustedLossPct: adjustedLosses.length > 0 ? totalAdjustedLossPct / adjustedLosses.length : 0,
      largestAdjustedWinPct: adjustedWins.length > 0 ? Math.max(...adjustedWins.map(getAdjustedPct)) : 0,
      largestAdjustedLossPct: adjustedLosses.length > 0 ? Math.abs(Math.min(...adjustedLosses.map(getAdjustedPct))) : 0,
      adjustedProfitFactor: totalAdjustedLossPct > 0 ? totalAdjustedWinPct / totalAdjustedLossPct : (totalAdjustedWinPct > 0 ? Infinity : 0),
      avgDurationMins: durationCount > 0 ? totalDurationMs / durationCount / 60000 : 0,
      maxWinStreak,
      maxLossStreak,
    };
  }, [trades, stats.slippageCostPct]);

  const fmtDuration = (mins) => {
    if (!mins) return '—';
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatPercent = (value, showSign = true) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    const num = Number(value);
    const sign = showSign ? (num >= 0 ? '+' : '') : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const netReturnColor = stats.totalAdjustedPnLPct >= 0 ? COLORS.success : COLORS.danger;
  const winRateColor = parseFloat(stats.adjustedWinRate) >= 50 ? COLORS.success : COLORS.danger;
  const profitFactorColor = computedStats.adjustedProfitFactor >= 1 ? COLORS.success : COLORS.danger;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Net Return Header */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          pb: 1, 
          mb: 0.75, 
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Typography 
          sx={{ 
            color: netReturnColor, 
            fontSize: '1.4rem', 
            fontWeight: 700, 
            fontFamily: 'monospace',
            lineHeight: 1,
          }}
        >
          {formatPercent(stats.totalAdjustedPnLPct)}
        </Typography>
        <Typography sx={{ color: COLORS.text.muted, fontSize: '0.55rem', mt: 0.25 }}>
          Net Return (after {(stats.slippageCostPct || 0.05) * 100}% costs)
        </Typography>
      </Box>

      {/* Stats List */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <StatRow 
          label="Total Trades" 
          value={stats.total} 
        />
        <StatRow 
          label="Win Rate" 
          value={stats.adjustedWinRate} 
          valueColor={winRateColor}
          suffix="%" 
        />
        <StatRow 
          label="Profit Factor" 
          value={computedStats.adjustedProfitFactor === Infinity ? '∞' : computedStats.adjustedProfitFactor.toFixed(2)} 
          valueColor={profitFactorColor}
        />
        <StatRow 
          label="Winners" 
          value={stats.adjustedWinning} 
          valueColor={COLORS.success}
        />
        <StatRow 
          label="Losers" 
          value={stats.adjustedLosing} 
          valueColor={COLORS.danger}
        />
        <StatRow 
          label="Avg Win" 
          value={`+${computedStats.avgAdjustedWinPct.toFixed(2)}`} 
          valueColor={COLORS.success}
          suffix="%" 
        />
        <StatRow 
          label="Avg Loss" 
          value={`-${Math.abs(computedStats.avgAdjustedLossPct).toFixed(2)}`} 
          valueColor={COLORS.danger}
          suffix="%" 
        />
        <StatRow 
          label="Best Trade" 
          value={`+${computedStats.largestAdjustedWinPct.toFixed(2)}`} 
          valueColor={COLORS.success}
          suffix="%" 
        />
        <StatRow 
          label="Worst Trade" 
          value={`-${Math.abs(computedStats.largestAdjustedLossPct).toFixed(2)}`} 
          valueColor={COLORS.danger}
          suffix="%" 
        />
        <StatRow 
          label="Avg Duration" 
          value={fmtDuration(computedStats.avgDurationMins)} 
        />
        <StatRow 
          label="Max Win Streak" 
          value={computedStats.maxWinStreak} 
          valueColor={COLORS.success}
        />
        <StatRow 
          label="Max Loss Streak" 
          value={computedStats.maxLossStreak} 
          valueColor={COLORS.danger}
          isLast
        />
      </Box>

      {/* Footer with raw return */}
      <Box 
        sx={{ 
          pt: 0.75, 
          borderTop: `1px solid ${COLORS.border}`,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: COLORS.text.muted, fontSize: '0.55rem' }}>
          Raw Return: <span style={{ color: stats.totalPnLPct >= 0 ? COLORS.success : COLORS.danger, fontWeight: 600 }}>{formatPercent(stats.totalPnLPct)}</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default TradeSummary;
